import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useStores } from "@/stores";
import type { RoleInstance } from "@/stores/admin/models";

interface EditRoleDialogProps {
  role: RoleInstance | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SCOPE_OPTIONS = [
  { value: "org", label: "Org" },
  { value: "building", label: "Building" },
  { value: "project", label: "Project" },
];

export function EditRoleDialog({ role, open, onOpenChange }: EditRoleDialogProps) {
  const { rolesStore } = useStores();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState("org");
  const [deprecated, setDeprecated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (role && open) {
      setName(role.name);
      setDescription(role.description ?? "");
      setScope(role.scope);
      setDeprecated(role.deprecated);
    }
  }, [role, open]);

  if (!role) return null;

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Role name is required");
      return;
    }

    setIsSaving(true);
    try {
      await rolesStore.updateRole(role.code, {
        name: trimmedName,
        description: description.trim() || null,
        scope,
        deprecated,
      });
      toast.success(`Role "${trimmedName}" updated`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update role");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Edit role</DialogTitle>
          <DialogDescription>
            Update the display name, scope, and description. The role code is the stable identifier
            referenced from <code>role_permissions</code> and <code>org_user_roles</code> and
            cannot be changed.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Code</Label>
            <Input value={role.code} disabled className="font-mono" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit_role_name">
              Display name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit_role_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit_role_scope">Scope</Label>
            <Select value={scope} onValueChange={setScope} disabled={isSaving}>
              <SelectTrigger id="edit_role_scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit_role_description">Description</Label>
            <Textarea
              id="edit_role_description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[80px]"
              disabled={isSaving}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={deprecated}
              onCheckedChange={(c) => setDeprecated(c === true)}
              disabled={isSaving}
            />
            <span>Deprecated (hide from new role assignments)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
